/**
 * MCQ (Multiple Choice Question) utility functions
 * Used by QuestionEditPage, quiz-carousel, and homework-preview-dialog
 */

import { processBackslashes } from "./preprocess";

export interface MCQOption {
  label: 'A' | 'B' | 'C' | 'D';
  content: string;       // Chinese LaTeX content
  contentEn: string;     // English LaTeX content
  isCorrect: boolean;    // Whether this option is a correct answer
}

/** Default empty MCQ options (A–D) */
export function createDefaultMCQOptions(): MCQOption[] {
  return [
    { label: 'A', content: '', contentEn: '', isCorrect: false },
    { label: 'B', content: '', contentEn: '', isCorrect: false },
    { label: 'C', content: '', contentEn: '', isCorrect: false },
    { label: 'D', content: '', contentEn: '', isCorrect: false },
  ];
}

/**
 * Parse MCQ questionText to extract stem and options.
 * Unified function that replaces both parseMCQFromText and the inline parseMultipleChoiceOptions.
 *
 * @param questionText The full question text containing MCQ options
 * @param standardAnswer Optional correct answer string (e.g., "A", "BD") to mark isCorrect
 * @returns `{ stem, options }` where options have label and content, or `null` if no MCQ format detected
 */
export function parseMultipleChoiceOptions(
  questionText: string,
  standardAnswer?: string,
): { stem: string; options: MCQOption[] } | null {
  if (!questionText) return null;

  questionText = processBackslashes(questionText);

  const patterns = [
    /(?:^|\n\s*)([A-E])\)\s*([^]*?)(?=\n[A-E]\)|$)/g,  // A) B) C) D) E)
    /(?:^|\n\s*)\(([A-E])\)\s*([^(]*?)(?=\n\([A-E]\)|$)/g, // (A) (B) (C) (D) (E)
    /(?:^|\n\s*)([A-E])：\s*([^]*?)(?=\n[A-E]：|$)/g,   // A： B： C： D： E：
    /(?:^|\n\s*)([A-E]):\s*([^]*?)(?=\n[A-E]:|$)/g,    // A: B: C: D: E:
    /(?:^|\n\s*)([A-E])\.\s*([^]*?)(?=\n\s*[A-E]+\.|$)/g,  // A. B. C. D. E.
  ];

  const correctLetters = standardAnswer
    ? new Set((standardAnswer).replace(/[\s,]/g, '').split(''))
    : new Set<string>();

  for (const pattern of patterns) {
    const matches = [...questionText.matchAll(pattern)];

    if (matches.length >= 2) {
      const firstMatch = matches[0];
      const firstOptionIndex = firstMatch.index!;

      let actualOptionStart = firstOptionIndex;
      if (firstOptionIndex > 0 && questionText[firstOptionIndex - 1] === '\n') {
        actualOptionStart = firstOptionIndex - 1;
      }

      const stem = questionText.substring(0, actualOptionStart).trim();

      const parsedOptions = new Map<string, string>();
      for (const match of matches) {
        parsedOptions.set(match[1], match[2].trim());
      }

      const options: MCQOption[] = (['A', 'B', 'C', 'D'] as const).map((label) => ({
        label,
        content: parsedOptions.get(label) || '',
        contentEn: '',
        isCorrect: correctLetters.has(label),
      }));

      return { stem, options };
    }
  }

  return null;
}

/**
 * Build the MCQ options portion of questionText from MCQ options.
 * E.g., "A. option1\nB. option2\nC. option3\nD. option4"
 *
 * @param options MCQ options array
 * @param lang 'zh' for Chinese content, 'en' for English content
 */
export function buildOptionsTextFromMCQ(
  options: MCQOption[],
  lang: 'zh' | 'en',
): string {
  return options
    .filter((o) => {
      const text = lang === 'en' ? o.contentEn : o.content;
      return text.trim().length > 0;
    })
    .map((o) => `${o.label}. ${lang === 'en' ? o.contentEn : o.content}`)
    .join('\n');
}

/**
 * Build the full questionText from stem + MCQ options.
 */
export function buildQuestionTextFromMCQ(
  stem: string,
  options: MCQOption[],
  lang: 'zh' | 'en',
): string {
  console.log("buildQuestionTextFromMCQ")
  const optionsText = buildOptionsTextFromMCQ(options, lang);
  if (!optionsText) return stem;
  if (!stem) return optionsText;
  return stem + '\n' + optionsText;
}

/**
 * Build the answerText from MCQ options (sorted correct letter string).
 * E.g., "A" or "BD"
 */
export function buildAnswerTextFromMCQ(options: MCQOption[]): string {
  return options
    .filter((o) => o.isCorrect)
    .map((o) => o.label)
    .sort()
    .join('');
}
